import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {
  HttpErrorResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';

import type { Observable } from 'rxjs/internal/Observable';
import { catchError, first, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UrlFixerService } from '..';
import { AuthService, interceptorSkipHeader } from 'src/app/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  public constructor(@Inject(Router) private readonly router: Router,
    @Inject(AuthService) private readonly authService: AuthService,
    @Inject(UrlFixerService) private readonly urlFixerService: UrlFixerService) { }

  public intercept(req: HttpRequest<any>,
    next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.startsWith(environment.apiBaseUrl)) {
      return next
        .handle(req)
        .pipe(catchError((err: Error) => this.errHandler(err)));
    }

    // skip interception if request has skip header
    if (req.headers.has(interceptorSkipHeader)) {
      return next.handle(req);
    }

    // Let auth service handle caching - it returns cached value if available, otherwise fetches
    return this.authService.getCurrentOrganization().pipe(
      first(),
      switchMap(org => {
        // Add org id header
        const requestWithOrgHeader = req.clone({
          setHeaders: {
            'X-Organization-Id': org?.id ?? '',
          },
        });

        if (!this.urlFixerService.needsOrgAddedToUrl(requestWithOrgHeader.url)) {
          return next
            .handle(requestWithOrgHeader)
            .pipe(catchError((err: Error) => this.errHandler(err)));
        }

        return this.urlFixerService.fixUrl(requestWithOrgHeader.url).pipe(
          switchMap((url: string) => {
            const httpRequest = requestWithOrgHeader.clone({ url });

            return next
              .handle(httpRequest)
              .pipe(catchError((err: Error) => this.errHandler(err)));
          }),
        );
      }),
    );
  }

  private redirectIfNotPage(url: string): void {
    if (!window.location.pathname.startsWith(url)) {
      this.router.navigateByUrl(url);
    }
  }

  private errHandler(err: Error): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 401) {
        console.log('intercept 401 redirect', err);
        this.redirectIfNotPage('login');
      }
    }
    return throwError(() => err);
  }

}
